import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ChevronLeft, ChevronRight, FlaskConical, Info, Landmark, Network, RefreshCw, Search, Shield, Trophy, UserCheck,
  Users, X,
} from 'lucide-react';
import { apiService, type FactionsResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { BarList, ColumnChart, DonutChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay } from './jobStats';
import { formatMoney, medal, pageLabel } from './moneyLeaderboard';
import {
  BRANCH_ORDER, branchColor, branchLabel, type FactionSort, filterFactions, formatMoneyShort, leagueLabel, percentLabel,
  sectionError, sortFactions,
} from './shopFactions';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';

const PAGE = 25;

const SORTS: { key: FactionSort; label: string }[] = [
  { key: 'techLevel', label: "Niveau d'arbre" },
  { key: 'members', label: 'Membres' },
  { key: 'bank', label: 'Banque' },
  { key: 'experience', label: 'Expérience' },
  { key: 'seasonalElo', label: 'Elo de saison' },
  { key: 'createdAt', label: 'Les plus récentes' },
  { key: 'name', label: 'Nom' },
];

const LEAGUE_COLORS: Record<string, string> = {
  unranked: 'var(--job-other)', bronze: 'var(--job-2)', silver: 'var(--job-1)', gold: 'var(--job-4)',
  platinum: 'var(--job-3)', diamond: 'var(--job-7)',
};

/**
 * Section « Factions » de l'espace Statistiques (Paladium) : factions de la saison (sans Wilderness, SafeZone ni
 * WarZone), membres, joueurs en faction (satellite 1.0.28), niveau d'arbre de techno, banque, ligues, créations,
 * évolution et tableau des factions. Lu par le satellite (1.0.26) dans la base de FactionApi.
 */
export function FactionsSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const [data, setData] = useState<FactionsResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<FactionSort>('techLevel');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getFactionStats();
    if (res.data) { setData(res.data); setError(null); }
    else { setData(null); setError({ status: res.status, message: sectionError(res.status, res.error, 'les statistiques des factions') }); }
    setLoading(false);
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(0); }, [sort, query]);

  const rows = useMemo(() => sortFactions(filterFactions(data?.table ?? [], query), sort), [data, sort, query]);

  if (loading && !data && !error) return <div className={lb.state}><div className={lb.spinner} /><p>Lecture des factions...</p></div>;
  if (error && !data) {
    return (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{error.status === 404 ? 'Statistiques des factions pas encore servies' : 'Factions illisibles'}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) return <div className={lb.state}><Shield size={32} /><h3>Pas de factions ici</h3><p>{data.reason}</p></div>;

  const tech = data.tech!;
  const bank = data.bank!;
  const cards = [
    { icon: Shield, value: data.factions.toLocaleString('fr-FR'), label: 'Factions', sub: data.season ? `Saison ${data.season.number}` : 'Sans les trois par défaut' },
    { icon: Users, value: data.members.toLocaleString('fr-FR'), label: 'Membres', sub: `${data.averageMembers.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} par faction, ${data.maxMembers} au plus` },
    { icon: Network, value: tech.averageLevel.toLocaleString('fr-FR', { maximumFractionDigits: 1 }), label: "Niveau d'arbre moyen", sub: `${tech.maxLevel} au plus, ${tech.researching} en recherche` },
    { icon: Landmark, value: formatMoneyShort(bank.total), label: 'En banque', sub: `${formatMoneyShort(bank.average)} en moyenne` },
  ];
  const sizeCols: ColumnDatum[] = data.sizes.map((s) => ({
    key: s.label, label: s.label, title: `${s.label} membre${s.label === '1' ? '' : 's'}`,
    segments: [{ id: 'f', label: 'Factions', value: s.factions, color: 'var(--job-1)' }],
  }));
  const levelCols: ColumnDatum[] = tech.levels.map((l) => ({
    key: String(l.level), label: String(l.level), title: `Niveau ${l.level}`,
    segments: [{ id: 'f', label: 'Factions', value: l.factions, color: 'var(--job-7)' }],
  }));
  const createdCols: ColumnDatum[] = data.created.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: [{ id: 'f', label: 'Factions créées', value: d.factions, color: 'var(--job-3)' }],
  }));
  const historyCols = (pick: (h: FactionsResponse['history'][number]) => number, label: string, color: string): ColumnDatum[] =>
    data.history.map((h) => ({
      key: h.date, label: shortDay(h.date), title: `${dayLabel(h.date)}${h.season ? `, saison ${h.season}` : ''}`,
      segments: [{ id: 'v', label, value: pick(h), color }],
    }));
  const count = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} faction${n > 1 ? 's' : ''}`;
  const playerCount = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} joueur${n > 1 ? 's' : ''}`;
  const players = data.players;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <div className={styles.section}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Factions de la saison en cours{data.season ? ` (saison ${data.season.number}, commencée le ${formatInstant(data.season.startedAt).split(' à ')[0]})` : ''},
          sans Wilderness, SafeZone ni WarZone, lues en direct. Le niveau d'arbre est la somme des paliers terminés des
          quatre branches. Un début de saison remet tout à zéro.
        </span>
      </div>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><UserCheck size={18} /> Joueurs en faction</h3></div>
        {players ? (
          <>
            <div className={styles.grid2}>
              <div>
                <h4 className={styles.blockSub}>Joueurs déjà venus en jeu</h4>
                <DonutChart formatValue={playerCount} centerValue={percentLabel(players.inFaction, players.total)} centerLabel="en faction"
                  ariaLabel="Joueurs en faction et sans faction"
                  slices={[
                    { key: 'in', label: 'En faction', value: players.inFaction, color: 'var(--job-1)' },
                    { key: 'out', label: 'Sans faction', value: players.withoutFaction, color: 'var(--job-other)' },
                  ]} />
              </div>
              <div>
                <h4 className={styles.blockSub}>Joueurs actifs (vus ces 7 derniers jours)</h4>
                <DonutChart formatValue={playerCount} centerValue={percentLabel(players.activeInFaction, players.active)} centerLabel="en faction"
                  ariaLabel="Joueurs actifs en faction et sans faction"
                  slices={[
                    { key: 'in', label: 'En faction', value: players.activeInFaction, color: 'var(--job-1)' },
                    { key: 'out', label: 'Sans faction', value: players.activeWithoutFaction, color: 'var(--job-other)' },
                  ]} />
              </div>
            </div>
            <p className={styles.muted}>
              Joueurs connus de FactionApi, c'est-à-dire déjà venus en jeu, pas tous les comptes du réseau. Un joueur
              de Wilderness, SafeZone ou WarZone compte sans faction.
            </p>
          </>
        ) : (
          <p className={styles.muted}>Le satellite de ce projet ne donne pas encore les joueurs en faction : cela arrive avec le satellite 1.0.28.</p>
        )}
      </section>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Users size={18} /> Taille des factions</h3></div>
          <ColumnChart data={sizeCols} formatValue={count} formatTick={(n) => Math.round(n).toLocaleString('fr-FR')} ariaLabel="Factions par nombre de membres" height={190} />
          <div className={styles.statLine}>
            <span>Médiane <b>{data.medianMembers}</b> membres</span>
            <span>Ouvertes au recrutement <b>{data.openFactions.toLocaleString('fr-FR')}</b></span>
          </div>
        </section>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Network size={18} /> Niveau d'arbre de techno</h3></div>
          <ColumnChart data={levelCols} formatValue={count} formatTick={(n) => Math.round(n).toLocaleString('fr-FR')} ariaLabel="Factions par niveau d'arbre" height={190} />
          <BarList formatValue={(n) => `palier ${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}`}
            rows={tech.branches.map((b) => ({
              id: b.key, label: branchLabel(b.key), color: branchColor(b.key), value: b.averageTier,
              share: b.averageTier, sub: `moyen, ${b.maxTier} au plus`,
            }))} />
        </section>
      </div>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Trophy size={18} /> Ligues</h3></div>
          <DonutChart slices={data.leagues.map((l) => ({ key: l.league, label: leagueLabel(l.league), value: l.factions, color: LEAGUE_COLORS[l.league] ?? 'var(--job-other)' }))}
            formatValue={count} centerValue={data.factions.toLocaleString('fr-FR')} centerLabel="factions" ariaLabel="Factions par ligue" />
        </section>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Landmark size={18} /> Banque des factions</h3></div>
          <div className={styles.statLine}>
            <span>Total <b>{formatMoney(bank.total)}</b></span>
            <span>Moyenne <b>{formatMoney(Math.round(bank.average))}</b></span>
            <span>Médiane <b>{formatMoney(bank.median)}</b></span>
            <span>La plus riche <b>{formatMoney(bank.max)}</b></span>
          </div>
          <BarList formatValue={formatMoneyShort} rows={sortFactions(data.table, 'bank').slice(0, 6).map((f) => ({
            id: f.id, label: f.name ?? f.id, color: 'var(--job-4)', value: f.bank,
            share: bank.total > 0 ? (f.bank * 100) / bank.total : 0, sub: `${f.members} membres`,
          }))} />
        </section>
      </div>

      {(data.created.length > 0 || data.history.length > 0) && (
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><FlaskConical size={18} /> Évolution</h3></div>
          <div className={styles.grid2}>
            {data.created.length > 0 && (
              <div>
                <h4 className={styles.blockSub}>Factions créées par jour (factions encore en vie)</h4>
                <ColumnChart data={createdCols} formatValue={count} formatTick={(n) => Math.round(n).toLocaleString('fr-FR')} ariaLabel="Factions créées par jour" height={180} />
              </div>
            )}
            {data.history.length > 0 ? (
              <div>
                <h4 className={styles.blockSub}>Banque totale, relevé de chaque jour</h4>
                <ColumnChart data={historyCols((h) => h.bank, 'Banque totale', 'var(--job-4)')} formatValue={formatMoneyShort} ariaLabel="Banque totale par jour" height={180} />
              </div>
            ) : (
              <p className={styles.muted}>Le relevé de chaque jour commence avec le satellite 1.0.26 : l'évolution du nombre de factions et de la banque apparaîtra jour après jour.</p>
            )}
          </div>
        </section>
      )}

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><Shield size={18} /> Toutes les factions</h3>
          <span className={lb.tableCount}>{rows.length.toLocaleString('fr-FR')} faction{rows.length > 1 ? 's' : ''}, {pageLabel(page, pages)}</span>
        </div>
        <div className={lb.toolbar}>
          <label className={lb.search}>
            <Search size={16} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une faction par son nom" aria-label="Rechercher une faction" />
            {query && <button type="button" className={lb.clear} onClick={() => setQuery('')} title="Effacer la recherche"><X size={14} /></button>}
          </label>
          <select className={lb.select} value={sort} onChange={(e) => setSort(e.target.value as FactionSort)} aria-label="Trier par">
            {SORTS.map((s) => <option key={s.key} value={s.key}>Tri : {s.label}</option>)}
          </select>
        </div>
        {shown.length === 0 ? <p className={lb.empty}>{query ? 'Aucune faction de ce nom.' : 'Aucune faction cette saison.'}</p> : (
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead>
                <tr>
                  <th>#</th><th>Faction</th><th className={lb.num}>Membres</th><th className={lb.num}>Niveau</th><th>Branches</th>
                  <th className={lb.num}>Banque</th><th>Ligue</th><th className={lb.num}>Elo</th><th>Créée le</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((f, i) => {
                  const rank = page * PAGE + i + 1;
                  const m = !query ? medal(rank) : null;
                  return (
                    <tr key={f.id}>
                      <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{rank}</span></td>
                      <td><span className={lb.playerName}>{f.name ?? f.id}</span>{f.open && <span className={lb.playerSub}> ouverte</span>}</td>
                      <td className={lb.num}>{f.members}</td>
                      <td className={lb.num}><span className={styles.levelPill}>{f.techLevel}</span></td>
                      <td>
                        <div className={styles.miniJobs}>
                          {BRANCH_ORDER.map((b) => (
                            <span key={b}><span className={styles.swatch} style={{ background: branchColor(b) }} />{branchLabel(b)} {f.branches[b] ?? 0}</span>
                          ))}
                        </div>
                      </td>
                      <td className={lb.num}>{formatMoney(f.bank)}</td>
                      <td>{leagueLabel(f.league)}</td>
                      <td className={lb.num}>{f.seasonalElo.toLocaleString('fr-FR')}</td>
                      <td className={lb.nowrap}>{f.createdAt ? formatInstant(f.createdAt).split(' à ')[0] : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className={lb.pagination}>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft size={16} /> Précédent</button>
            <span>{pageLabel(page, pages)}</span>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= pages}>Suivant <ChevronRight size={16} /></button>
          </div>
        )}
      </section>
    </div>
  );
}
